import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AUTH_SESSION_EXPIRED_EVENT,
  clearApiCredentials,
  clearHostTenantId,
  clearTenantId,
  getHostTenantId,
  getResolvedTenantId,
  getSessionAuthToken,
  persistApiCredentials,
  persistAuthUser,
  persistHostTenantId,
  purgeLegacyAuthStorage,
  readAuthUserRaw,
  TENANT_CREDENTIALS_EVENT,
  withSuppressedSessionExpiry,
} from '../config/api-config';
import { apiService } from '../services/api';
import { clearApiAccessRules, setApiAccessRules } from '../services/api/apiAccessStore';
import { ApiError, formatApiError } from '../utils/apiError';
import type { User } from '../types/config';
import { resolveUserRole } from '../utils/roles';
import { normalizeModuleAccessAllowList } from '../utils/moduleAccess';
import { mergeFeesSubmodulesIntoUserModuleAccess } from '../config/platformFeeSubmoduleDefaults';
import {
  apexPlatformLoginOnlyMessage,
  sanitizeLoginErrorMessage,
  shouldTrySuperAdminLoginFirst,
} from '../utils/authLogin';
import { onTabVisibleRefresh } from '../utils/tabVisibilityRefresh';
import { isTenantHost, slugFromHostname } from '../utils/tenantSlug';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  moduleAccessError: string | null;
  login: (email: string, password: string) => Promise<User>;
  completeLoginFromResponse: (data: unknown, emailHint?: string) => Promise<User>;
  clearMustChangePassword: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function displayNameFromEmail(email: string): string {
  const local = email.split('@')[0] || 'User';
  return local
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function readStoredUser(): User | null {
  const raw = readAuthUserRaw();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as User;
    const role = resolveUserRole(
      { email: parsed.email, role: parsed.role, permissions: parsed.permissions },
      parsed.email
    );
    if (parsed?.id && parsed?.email && role) {
      return {
        ...parsed,
        role,
        moduleAccess: parsed.moduleAccess?.length
          ? normalizeModuleAccessAllowList(parsed.moduleAccess)
          : parsed.moduleAccess,
      };
    }
  } catch {
    /* ignore */
  }
  return null;
}

async function loadDbModuleAccess(): Promise<{
  modules: NonNullable<User['moduleAccess']>;
  roleNames: string[];
  mustChangePassword: boolean;
}> {
  const accessRes = await apiService.getMyModuleAccess();
  const roleNames = Array.isArray(accessRes.data?.roles)
    ? accessRes.data.roles.filter((r): r is string => typeof r === 'string' && r.trim().length > 0)
    : [];
  return {
    modules: normalizeModuleAccessAllowList(
      mergeFeesSubmodulesIntoUserModuleAccess(accessRes.data?.modules ?? [])
    ),
    roleNames,
    mustChangePassword: Boolean(accessRes.data?.must_change_password),
  };
}

function moduleAccessErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return `${error.displayMessage()} Ask an administrator to enable access for this role.`;
    }
    if (error.status === 401) {
      return error.displayMessage();
    }
  }
  return formatApiError(error, 'Could not load module access.');
}

/** Endpoint rules from DB (GET /users/me/api-access) gate every ApiClient call. */
async function refreshApiAccessRules(): Promise<void> {
  try {
    const res = await apiService.getMyApiAccess();
    setApiAccessRules(res.data?.rules ?? []);
  } catch {
    // Fail-open: without rules the client calls normally and the server enforces.
    clearApiAccessRules();
  }
}

async function attachDbModuleAccess(user: User): Promise<User> {
  if (user.role === 'super_admin') {
    clearApiAccessRules();
    return user;
  }
  const existing = normalizeModuleAccessAllowList(user.moduleAccess ?? []);
  // Load endpoint rules before module_access — stale rules in localStorage must not
  // client-block GET /users/me/module-access during session restore (portal users).
  await refreshApiAccessRules();
  const loaded = await loadDbModuleAccess();
  // Role module_access from DB is authoritative for every tenant role (including admin/owner).
  // Do not auto-grant all tenant subscriptions by role name — configure via Permissions.
  let latest = loaded.modules;
  if (!latest.length) {
    latest = existing;
  }

  return {
    ...user,
    mustChangePassword: loaded.mustChangePassword,
    ...(loaded.roleNames.length ? { roleNames: loaded.roleNames } : {}),
    ...(latest.length ? { moduleAccess: latest } : {}),
  };
}

function persistUser(user: User): void {
  persistAuthUser(JSON.stringify(user));
}

async function validateStoredSession(user: User): Promise<boolean> {
  const token = getSessionAuthToken();
  if (!token) return false;
  if (user.role === 'super_admin') {
    await apiService.listSuperAdminTenants();
    return true;
  }
  const tenant = getResolvedTenantId().trim();
  if (!tenant || !/^\d+$/.test(tenant)) return false;
  // Probe must not fire AUTH_SESSION_EXPIRED — restoreSession decides whether to clear.
  await apiService.getTenantSettings({ suppressSessionExpiry: true });
  return true;
}

function resolveLoginRole(data: unknown, email: string): User['role'] {
  const body = data as {
    role?: string | null;
    user_role?: string | null;
    role_name?: string | null;
    roles?: string[] | null;
    permissions?: string[] | null;
  };
  return resolveUserRole(
    {
      email,
      role:
        body.role ??
        body.user_role ??
        body.role_name ??
        body.roles?.[0] ??
        undefined,
      permissions: resolveLoginPermissions(data),
    },
    email
  );
}

async function resolveHostTenantFromLocation(): Promise<void> {
  if (typeof window === 'undefined') return;
  const slug = slugFromHostname(window.location.hostname);
  if (!slug) {
    clearHostTenantId();
    return;
  }
  try {
    const res = await apiService.resolveTenant({ slug });
    const tenantId = Number(res.data?.tenant_id);
    if (Number.isFinite(tenantId) && tenantId > 0) {
      persistHostTenantId(String(tenantId));
    } else {
      clearHostTenantId();
    }
  } catch {
    clearHostTenantId();
  }
}

async function authenticateUser(email: string, password: string): Promise<unknown> {
  const loginEmail = email.trim();
  // School URL must resolve before tenant login — credentials are never looked up globally.
  if (isTenantHost() && !getHostTenantId()) {
    await resolveHostTenantFromLocation();
  }
  const hostTenantRaw = getHostTenantId();
  const hostTenantId =
    hostTenantRaw && /^\d+$/.test(hostTenantRaw) ? Number(hostTenantRaw) : undefined;
  const credentials = {
    email: loginEmail,
    password,
    ...(hostTenantId ? { tenant_id: hostTenantId } : {}),
  };

  // Suppress session-expiry while clearing + logging in so TENANT_CREDENTIALS listeners
  // and a failed parallel call cannot wipe credentials mid-login.
  return withSuppressedSessionExpiry(async () => {
    clearApiCredentials();

    const tryTenantLogin = () => {
      if (!hostTenantId) {
        return Promise.reject(new Error('Open the login page from your school URL.'));
      }
      return apiService.loginBasic(credentials).then((response) => response.data);
    };
    const trySuperAdminLogin = () =>
      apiService.loginSuperAdmin(credentials).then((response) => response.data);

    // Apex / www / localhost: platform console only — never accept school (tenant) login.
    if (!isTenantHost()) {
      try {
        return await trySuperAdminLogin();
      } catch {
        throw new Error(sanitizeLoginErrorMessage(null));
      }
    }

    try {
      // School portal: only try platform super-admin when the email looks like one.
      // Otherwise a failed tenant login was falling through to /super-admin/login and
      // masking the real 401 (wrong password / missing membership).
      if (shouldTrySuperAdminLoginFirst(loginEmail)) {
        try {
          return await trySuperAdminLogin();
        } catch {
          return await tryTenantLogin();
        }
      }

      return await tryTenantLogin();
    } catch (err) {
      if (err instanceof Error && err.message.includes('school URL')) {
        throw err;
      }
      throw new Error(sanitizeLoginErrorMessage(null));
    }
  });
}

function resolveLoginToken(data: unknown): string {
  const body = data as {
    session_token?: string | null;
    token?: string | null;
    access_token?: string | null;
  };
  return (body.session_token || body.token || body.access_token || '').trim();
}

function resolveLoginTenantId(data: unknown): number | null {
  const body = data as { tenant_id?: number | string | null };
  if (body.tenant_id == null) return null;
  const tenantId = Number(body.tenant_id);
  return Number.isFinite(tenantId) ? tenantId : null;
}

function loginField(data: unknown, key: 'email' | 'name' | 'user_id'): string | null {
  const body = data as Record<string, unknown>;
  const value = body[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function resolveLoginPermissions(data: unknown): string[] {
  const body = data as { permissions?: unknown };
  if (!Array.isArray(body.permissions)) return [];
  return body.permissions.filter((permission): permission is string => typeof permission === 'string');
}

function resolveLoginRoleNames(data: unknown): string[] {
  const body = data as { roles?: unknown; role_name?: unknown; role?: unknown };
  const fromList = Array.isArray(body.roles)
    ? body.roles.filter((r): r is string => typeof r === 'string' && r.trim().length > 0)
    : [];
  if (fromList.length) return fromList;
  const single =
    (typeof body.role_name === 'string' && body.role_name.trim()) ||
    (typeof body.role === 'string' && body.role.trim()) ||
    '';
  return single ? [single] : [];
}

function resolveLoginModuleAccess(data: unknown) {
  const body = data as { module_access?: unknown };
  if (!Array.isArray(body.module_access)) return [];
  const parsed = body.module_access.filter(
    (entry): entry is NonNullable<User['moduleAccess']>[number] =>
      typeof entry === 'object' &&
      entry != null &&
      typeof (entry as { module_code?: unknown }).module_code === 'string'
  );
  return normalizeModuleAccessAllowList(parsed);
}

function resolveMustChangePassword(data: unknown): boolean {
  const body = data as { must_change_password?: unknown };
  return body.must_change_password === true;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [moduleAccessError, setModuleAccessError] = useState<string | null>(null);

  useEffect(() => {
    purgeLegacyAuthStorage();
    let cancelled = false;

    async function restoreSession() {
      await resolveHostTenantFromLocation();
      if (cancelled) return;

      const storedUser = readStoredUser();
      if (!storedUser) {
        clearApiCredentials();
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      try {
        const valid = await withSuppressedSessionExpiry(() => validateStoredSession(storedUser));
        if (!valid) {
          clearApiCredentials();
        } else if (storedUser.role === 'super_admin') {
          clearTenantId();
        } else if (!isTenantHost()) {
          // Apex must not keep school-user sessions.
          clearApiCredentials();
          if (!cancelled) {
            setUser(null);
            setLoading(false);
          }
          return;
        }
        if (!cancelled) {
          if (valid && storedUser.role !== 'super_admin') {
            try {
              // Mirror login: never let a previous session's endpoint rules gate restore.
              clearApiAccessRules();
              const enriched = await withSuppressedSessionExpiry(() =>
                attachDbModuleAccess(storedUser)
              );
              persistUser(enriched);
              setUser(enriched);
              setModuleAccessError(null);
            } catch (accessError) {
              const isUnauthorized =
                accessError instanceof ApiError && accessError.status === 401;
              if (isUnauthorized) {
                clearApiCredentials();
                setUser(null);
              } else {
                setModuleAccessError(moduleAccessErrorMessage(accessError));
                setUser(storedUser);
              }
            }
          } else {
            setUser(valid ? storedUser : null);
            setModuleAccessError(null);
          }
        }
      } catch (err) {
        // CORS / network / transient failures must not wipe a still-valid local session.
        // Only definitive auth rejection (401) clears credentials.
        const isUnauthorized = err instanceof ApiError && err.status === 401;
        if (isUnauthorized) {
          clearApiCredentials();
          if (!cancelled) {
            setUser(null);
          }
        } else if (!cancelled) {
          console.warn(
            '[auth] Session validation failed (network/CORS); keeping stored session:',
            err
          );
          setUser(storedUser);
          setModuleAccessError(
            'Could not verify your session with the server. Some data may be unavailable until connectivity is restored.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onSessionExpired = () => {
      clearApiCredentials();
      clearApiAccessRules();
      setUser(null);
      setModuleAccessError('Your login session has expired. Please log in again.');
    };
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  useEffect(() => {
    if (!user || user.role === 'super_admin') return undefined;
    let cancelled = false;

    async function refreshModuleAccess() {
      // Credential clears during login fire TENANT_CREDENTIALS_EVENT with no token —
      // skip until a session token is present again.
      if (!getSessionAuthToken()) return;
      try {
        const refreshed = await attachDbModuleAccess(user);
        if (cancelled) return;
        persistUser(refreshed);
        setUser(refreshed);
        setModuleAccessError(null);
      } catch (accessError) {
        if (!cancelled) {
          const isAuthError =
            accessError instanceof ApiError &&
            (accessError.status === 401 || accessError.status === 403);
          const hasCachedAccess = (user.moduleAccess?.length ?? 0) > 0;
          if (isAuthError || !hasCachedAccess) {
            setModuleAccessError(moduleAccessErrorMessage(accessError));
          }
        }
      }
    }

    window.addEventListener(TENANT_CREDENTIALS_EVENT, refreshModuleAccess);
    const stopTabRefresh = onTabVisibleRefresh(refreshModuleAccess);
    return () => {
      cancelled = true;
      window.removeEventListener(TENANT_CREDENTIALS_EVENT, refreshModuleAccess);
      stopTabRefresh();
    };
  }, [user]);

  const establishSessionFromLoginData = useCallback(
    async (data: unknown, emailHint?: string): Promise<User> => {
      return withSuppressedSessionExpiry(async () => {
        const hint = (emailHint || '').trim();
        const sessionToken = resolveLoginToken(data);
        const role = resolveLoginRole(data, hint);
        const tenantId = resolveLoginTenantId(data);
        if (!sessionToken) {
          throw new Error('Login response missing token');
        }
        if (role !== 'super_admin' && tenantId == null) {
          throw new Error('Login response missing tenant id');
        }
        if (!isTenantHost() && role !== 'super_admin') {
          clearApiCredentials();
          throw new Error(apexPlatformLoginOnlyMessage());
        }
        clearApiCredentials();
        // Never let a previous user's endpoint rules gate the new session.
        clearApiAccessRules();
        persistApiCredentials(sessionToken, tenantId == null ? undefined : String(tenantId));
        if (role === 'super_admin') {
          clearTenantId();
        }
        const resolvedEmail =
          loginField(data, 'email')?.toLowerCase() || hint.toLowerCase() || 'user@unknown';
        let nextUser: User = {
          id: loginField(data, 'user_id') || hint || resolvedEmail,
          email: resolvedEmail,
          name: loginField(data, 'name') || displayNameFromEmail(resolvedEmail),
          role,
          permissions: resolveLoginPermissions(data),
          roleNames: resolveLoginRoleNames(data),
          moduleAccess: resolveLoginModuleAccess(data),
          mustChangePassword: resolveMustChangePassword(data),
        };
        try {
          nextUser = await attachDbModuleAccess(nextUser);
          setModuleAccessError(null);
        } catch (accessError) {
          setModuleAccessError(moduleAccessErrorMessage(accessError));
        }
        persistUser(nextUser);
        setUser(nextUser);
        return nextUser;
      });
    },
    []
  );

  const login = useCallback(
    async (email: string, password: string): Promise<User> => {
      const loginEmail = email.trim();
      const data = await authenticateUser(loginEmail, password);
      return establishSessionFromLoginData(data, loginEmail);
    },
    [establishSessionFromLoginData]
  );

  const completeLoginFromResponse = useCallback(
    async (data: unknown, emailHint?: string): Promise<User> => {
      return establishSessionFromLoginData(data, emailHint);
    },
    [establishSessionFromLoginData]
  );

  const clearMustChangePassword = useCallback(() => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, mustChangePassword: false };
      persistUser(next);
      return next;
    });
  }, []);

  const logout = useCallback(() => {
    // Revoke server-side session while the bearer token is still available to the client.
    if (getSessionAuthToken()) {
      void apiService.logout().catch(() => undefined);
    }
    clearApiCredentials();
    clearApiAccessRules();
    setUser(null);
    setModuleAccessError(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user && getSessionAuthToken()),
      loading,
      moduleAccessError,
      login,
      completeLoginFromResponse,
      clearMustChangePassword,
      logout,
    }),
    [
      user,
      loading,
      moduleAccessError,
      login,
      completeLoginFromResponse,
      clearMustChangePassword,
      logout,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
